// Backend ADR 25.9.26n §10/§12 — the learner's own grants: `GET /oauth-grants`
// and `DELETE /oauth-grants/{id}`, in the feature rather than in `api/` (15/15,
// O2 §4's precedent).
//
// One query key, `['oauth-grants']`, read by two callers: the console's gate
// (`useAdminFacts`, which needs to know whether a live grant exists) and the
// Connected-apps page (which draws the list). Sharing the key — and the 60 s
// `staleTime` — is what makes the gate and the page one request, and keeps a
// walk between `/account` and `/admin` from repeating it.
//
// The list is never flag-gated (§10): a learner can always see what to remove.
// The response is untrusted JSON, so a row missing `id` or `client_name` is
// dropped whole here, and every other field folds to a safe reading.

import { ApiError, authed, authedJson } from '../../../api/http'

export interface ConnectedApp {
  id: string
  client_name: string
  /** The host the app sends people back to; `null` when the server has none to show. */
  redirect_host: string | null
  /** Wire strings; known ones are drawn with the consent screen's words. */
  scopes: string[]
  created_at: string
  last_used_at: string | null
  /** An install holds this grant, so removing it uninstalls the app too (§16). */
  installed: boolean
}

export const OAUTH_GRANTS_QUERY_KEY = ['oauth-grants'] as const

/** §12 — one read per minute at most, whichever of the gate or the page asks first. */
export const OAUTH_GRANTS_STALE_MS = 60_000

type Wire = Record<string, unknown>

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/** One grant row, or `null` without `id` or `client_name`. */
function narrowGrant(value: unknown): ConnectedApp | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const row = value as Wire
  const id = str(row.id)
  const name = str(row.client_name)
  if (id === null || name === null) return null
  return {
    id,
    client_name: name,
    redirect_host: str(row.redirect_host),
    scopes: Array.isArray(row.scopes) ? row.scopes.filter((scope): scope is string => typeof scope === 'string') : [],
    created_at: str(row.created_at) ?? '',
    last_used_at: str(row.last_used_at),
    installed: row.installed === true,
  }
}

function narrowGrants(value: unknown): ConnectedApp[] {
  return Array.isArray(value) ? value.map(narrowGrant).filter((row): row is ConnectedApp => row !== null) : []
}

export async function listGrants(): Promise<ConnectedApp[]> {
  return narrowGrants(await authedJson<unknown>('/oauth-grants'))
}

/** `204`, and `404 oauth_grant_not_found` alike: the grant is gone either way. Any other failure throws. */
export async function revokeGrant(id: string): Promise<void> {
  try {
    await authed(`/oauth-grants/${encodeURIComponent(id)}`, { method: 'DELETE' })
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error
  }
}
